import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import useVideoRecorder from '../../../../../HOC/VideoRecorderProvider';
import { Booth } from '../../../../../interfaces/interfaces';
import { createDisplayLink } from '../../../../../services/helper';
import { fileUpload } from '../../../../../services/services';
import VyButton from '../../../../common/VyButton';

// const boothVideoRecorder = new VideoRecorder();

interface VideoComponentProps {
  currentVideo: string;
  thumbnail?: string | null;
  updateVideo: (updatedVideoString: string) => void;
  booth: Booth;
}

const VideoComponent = ({ currentVideo, updateVideo, thumbnail }: VideoComponentProps) => {
  const { t } = useTranslation(['booth', 'common']);

  const [boothVideoRecorder, mediaState] = useVideoRecorder();
  const [playing, setPlaying] = useState(false);
  const video = useRef<HTMLVideoElement | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [uploading, setUploading] = useState(false);

  const isSafari = !!navigator.vendor.match(/apple/i);

  async function startRecordingProcessAndUpload() {
    const stream = await getUserMediaStream();
    if (!stream) return;
    boothVideoRecorder.setStream(stream);
    boothVideoRecorder.startRecording();
    if (video.current) video.current.srcObject = stream;
  }

  function stopRecording() {
    boothVideoRecorder.stopRecording();
  }

  // this is triggered when the video data is ready.
  boothVideoRecorder.onVideoReady(async (videoFile) => {
    setUploading(true);

    try {
      const data = await fileUpload(videoFile);
      updateVideo(data.data.fileId);
      setUploading(false);
    } catch (err) {
      console.error(err);
    }
  });

  function getUserMediaStream() {
    return navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720, frameRate: 15 }, audio: true });
  }

  function toggleVideo({ currentTarget }: React.MouseEvent<HTMLVideoElement, MouseEvent>) {
    if (currentTarget.paused) currentTarget.play().catch(() => {});
    else currentTarget.pause();
    setPlaying(!currentTarget.paused);
  }

  useEffect(() => {
    if (mediaState === 'recording') {
      setTimeout(() => setCountdown(countdown + 1), 1000);
      if (countdown > 30) stopRecording();
    } else {
      setCountdown(0);
    }
  }, [mediaState, countdown]);

  const notRecording = mediaState === 'inactive' || mediaState === null;

  return (
    <div className="relative w-full h-full">
      <video
        className={`top-1/2 translate-y-[-50%] max-w-full w-full absolute ${notRecording && 'hidden'}`}
        ref={video}
        autoPlay
        muted
        playsInline
        controls={false}
      />
      <div className="top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] absolute">
        <BeatLoader size={20} color="#4299e1" loading={uploading} />
      </div>
      {notRecording && !playing && !uploading && <FontAwesomeIcon icon={faPlay} className="play-button-video hover:cursor-pointer" />}
      {notRecording && currentVideo && (
        <video
          key={currentVideo}
          className="top-1/2 translate-y-[-50%] max-w-full absolute hover:cursor-pointer"
          onClick={toggleVideo}
          poster={createDisplayLink(thumbnail, false)}>
          <source src={`${process!.env!.baseURL! + process!.env!.basePath!}/file/display/${currentVideo}`} type="video/webm;codecs=vp8" />
          <em>Your browser doesn't support webm!</em>
        </video>
      )}

      <div className="absolute flex flex-row-reverse items-center justify-between w-full px-8 py-0 bottom-4" style={{ bottom: '1.5rem' }}>
        {mediaState === 'recording' && (
          <button className="px-2 py-1 text-base bg-blue-500 border-none rounded-md hover:bg-vystemblue" onClick={stopRecording}>
            Stop
          </button>
        )}
        {mediaState === 'recording' && (
          <div className="flex items-center justify-center">
            <div className="w-3 h-3 bg-red-600 rounded-full" />
            <span className="ml-2 text-white">{countdown}</span>
          </div>
        )}

        {notRecording && (
          <VyButton compact disabled={isSafari} onClick={startRecordingProcessAndUpload}>
            {t('booth:record')}
          </VyButton>
        )}
        {notRecording && currentVideo && (
          <VyButton compact onClick={() => updateVideo('')}>
            {t('common:delete')}
          </VyButton>
        )}
      </div>
      {isSafari && (
        <small className="absolute w-full text-center text-red" style={{ bottom: '0.25rem' }}>
          (recording is not available on Safari!)
        </small>
      )}
    </div>
  );
};

export default VideoComponent;
